import { jsxs, jsx } from 'react/jsx-runtime';
import * as React from 'react';
import styled from 'styled-components';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden.js';
import { Tooltip } from '../Tooltip/Tooltip.js';
import { BaseButton } from '../BaseButton/BaseButton.js';
import { Flex } from '../Flex/Flex.js';

const IconButton = React.forwardRef(({ label, background, borderWidth, noBorder = false, children, icon, disabled = false, onClick, 'aria-label': ariaLabel, ...restProps }, ref) => {
    const handleClick = (e) => {
        if (!disabled && onClick) {
            onClick(e);
        }
    };
    const component = (jsxs(IconButtonWrapper, { "aria-disabled": disabled, background: disabled ? 'neutral150' : background, borderWidth: noBorder ? 0 : borderWidth, justifyContent: "center", height: `${32 / 16}rem`, width: `${32 / 16}rem`, ...restProps, ref: ref, onClick: handleClick, children: [jsx(VisuallyHidden, { as: "span", children: label ?? ariaLabel }), React.cloneElement((icon || children), {
                'aria-hidden': true,
                focusable: false, // See: https://allyjs.io/tutorials/focusing-in-svg.html#making-svg-elements-focusable
            })] }));
    return label ? jsx(Tooltip, { label: label, children: component }) : component;
});
const IconButtonWrapper = styled(BaseButton) `
  svg {
    > g,
    path {
      fill: ${({ theme }) => theme.colors.neutral500};
    }
  }

  &:hover {
    svg {
      > g,
      path {
        fill: ${({ theme }) => theme.colors.neutral600};
      }
    }
  }

  &:active {
    svg {
      > g,
      path {
        fill: ${({ theme }) => theme.colors.neutral400};
      }
    }
  }

  &[aria-disabled='true'] {
    svg {
      path {
        fill: ${({ theme }) => theme.colors.neutral600};
      }
    }
  }
`;
const IconButtonGroup = styled(Flex) `
  & span:first-child button {
    border-left: 1px solid ${({ theme }) => theme.colors.neutral200};
    border-radius: ${({ theme }) => `${theme.borderRadius} 0 0 ${theme.borderRadius}`};
  }

  & span:last-child button {
    border-radius: ${({ theme }) => `0 ${theme.borderRadius} ${theme.borderRadius} 0`};
  }

  & ${IconButtonWrapper} {
    border-radius: 0;
    border-left: none;

    svg {
      path {
        fill: ${({ theme }) => theme.colors.neutral700};
      }
    }

    &:hover {
      background-color: ${({ theme }) => theme.colors.neutral100};

      svg {
        path {
          fill: ${({ theme }) => theme.colors.neutral800};
        }
      }
    }

    &:active {
      background-color: ${({ theme }) => theme.colors.neutral150};
      svg {
        path {
          fill: ${({ theme }) => theme.colors.neutral900};
        }
      }
    }

    &[aria-disabled='true'] {
      svg {
        path {
          fill: ${({ theme }) => theme.colors.neutral600};
        }
      }
    }
  }
`;

export { IconButton, IconButtonGroup };
